
import React, {FC, PropsWithChildren, useCallback, useEffect, useReducer, useState} from 'react';
import {useGeocodingCore} from '@mapbox/search-js-react';
import {MAPBOX_TOKEN} from 'components/BaseMap/constants';
import {calcRegion} from 'utils/calcRegion';
import {cookies, CookKey} from 'utils/cookies';
import {CoordsCallback, useUserCoords} from 'utils/geolocate';
import {findOrderRegionMetro} from 'utils/orderRegion';
import {AllRegions, Region} from 'utils/region';

import {regionReducer} from './reducer';
import {RegionContextValue, RegionSource} from './types';

// If geolocation hasn't answered by then, prices go ahead with what's known
const settleAfter = 3000;

const defaultValue: RegionContextValue = {
	state: {},
	orderSettled: false,
	chooseOrderRegion: () => {},
	setRegion: () => {},
	setCoords: () => {},
	clearRegion: () => {},
	skipGeolocation: () => {},
};

const RegionContext = React.createContext<RegionContextValue>(defaultValue);

export const useRegion = () => React.useContext(RegionContext);

// useChooseOrderRegion records the ordering region of a location the
// visitor is booking or checking out at, so their prices follow it
export const useChooseOrderRegion = (orderRegion?: string | null) => {
	const {chooseOrderRegion} = useRegion();

	useEffect(() => {
		if (orderRegion) {
			chooseOrderRegion(orderRegion);
		}
	}, [orderRegion]);
};

// Match the region cookie by key first, then by name (backward compat)
const cookieRegion = (): Region | undefined => {
	const cval = cookies.get(CookKey.Region);
	return cval ? AllRegions.find((r) => r.key === cval || r.name === cval) : undefined;
};

// Whether the ordering region may depend on where the visitor is: none
// saved, and no metro yet or one with several ordering regions
const needsLocation = (): boolean => {
	if (typeof window === 'undefined' || cookies.get(CookKey.OrderRegion)) {
		return false;
	}
	const region = cookieRegion();
	return !region || region.orderRegions.length > 1;
};

export const RegionProvider: FC<PropsWithChildren> = ({children}) => {
	const [state, dispatch] = useReducer(regionReducer, {});

	// On mount: read cookie and URL param
	useEffect(() => {
		// 1. Cookie
		const saved = cookieRegion();
		if (saved) {
			dispatch({type: 'SET_REGION', region: saved, source: 'COOKIE'});
		}

		// The ordering region decided on an earlier page view
		let orderRegion = cookies.get(CookKey.OrderRegion);

		// 2. URL param — match by key: a metro (nyc_metro), or an
		// ordering region (new_york)
		if (typeof window !== 'undefined') {
			const params = new URLSearchParams(window.location.search);
			const regionParam = params.get('region');
			if (regionParam) {
				const region = AllRegions.find((r: Region) => r.key === regionParam);
				const metro = region ? undefined : findOrderRegionMetro(regionParam);
				if (region) {
					dispatch({type: 'SET_REGION', region, source: 'URL'});

					// The metro picks its own ordering region, unless the
					// saved one is in it
					if (!region.orderRegions.some((o) => o.key === orderRegion)) {
						orderRegion = undefined;
					}
				} else if (metro) {
					// Keep the visitor's metro when the ordering region is in
					// it (texas from Dallas); otherwise its main metro stands
					// in until geolocation finds theirs
					if (!saved?.orderRegions.some((o) => o.key === regionParam)) {
						dispatch({type: 'SET_REGION', region: metro, source: 'ORDER'});
					}
					orderRegion = regionParam;
				}

				// Clean up URL param so refresh doesn't re-select
				const url = new URL(window.location.href);
				url.searchParams.delete('region');
				window.history.replaceState({}, '', url.toString());
			}
		}

		if (orderRegion) {
			dispatch({type: 'SET_ORDER_REGION', orderRegion});
		}
		dispatch({type: 'READY'});
	}, []);

	// If geolocation never answers, settle with what's known
	useEffect(() => {
		if (!state.ready) {
			return;
		}

		const timer = setTimeout(() => dispatch({type: 'SETTLE_ORDER_REGION'}), settleAfter);
		return () => clearTimeout(timer);
	}, [state.ready]);

	// 3. Geolocation callback — GPS or IP fallback
	const geocode = useGeocodingCore({accessToken: MAPBOX_TOKEN});

	const cancelledRef = React.useRef(false);
	const geoSkippedRef = React.useRef(false);

	const geoCb: CoordsCallback = useCallback((coords, source) => {
		dispatch({type: 'GEO_COMPLETE', coords});

		if (geoSkippedRef.current) return;

		// If coords are invalid, skip auto-config
		if (!coords.lat && !coords.lng) {
			return;
		}

		const region = calcRegion(coords);
		if (!region) {
			return;
		}

		// IP source: set region only
		if (source === 'IP') {
			dispatch({
				type: 'SET_REGION',
				region,
				coords,
				source: source as RegionSource,
			});
			return;
		}

		// GPS source: immediately set coords if in same region,
		// then refine label via reverse geocode
		if (source === 'GPS') {
			dispatch({
				type: 'SET_COORDS',
				coords,
				label: region.name,
				skipIf: 'mismatched',
				source: 'GPS',
			});

			(async () => {
				const result = await geocode.reverse(coords, {
					country: 'US',
					limit: 1,
					types: 'address',
				});

				if (cancelledRef.current) {
					return;
				}

				const feature = result.features.find(
					(f) => f.properties.feature_type === 'address',
				);

				if (feature) {
					const label = feature.properties.name_preferred;
					dispatch({
						type: 'SET_COORDS',
						coords,
						label,
						skipIf: 'mismatched',
						source: 'GPS',
					});
				}
			})();
		}
	}, []);

	// Prices wait for the ordering region, so when it may depend on where
	// the visitor is, look up their IP location right away rather than
	// after the location prompt
	const [ipNow] = useState(needsLocation);
	useUserCoords(geoCb, geoSkippedRef, {ipNow});

	useEffect(() => {
		return () => { cancelledRef.current = true; };
	}, []);

	// Persist region key (not display name) to cookie on change.
	// The checkout store reads this cookie raw as `userRegion`
	// for price queries. On init we accept both key and name
	// for backward compatibility with old name-based cookies.
	useEffect(() => {
		const key = state.region?.key;
		if (key) {
			cookies.set(CookKey.Region, key, {expires: 90});
		} else {
			cookies.remove(CookKey.Region);
		}
	}, [state.region?.key]);

	// Remember the ordering region so later page views start with it
	useEffect(() => {
		if (state.orderSettled && state.orderRegion) {
			cookies.set(CookKey.OrderRegion, state.orderRegion, {expires: 90});
		}
	}, [state.orderSettled, state.orderRegion]);

	const chooseOrderRegion = useCallback((orderRegion: string) => {
		dispatch({type: 'SET_ORDER_REGION', orderRegion});
	}, []);

	const setRegion = useCallback((region: Region) => {
		dispatch({type: 'SET_REGION', region, source: 'MANUAL'});
	}, []);

	const setCoords = useCallback((coords: {lat: number; lng: number}, label?: string) => {
		dispatch({type: 'SET_COORDS', coords, label, source: 'MANUAL'});
	}, []);

	const clearRegion = useCallback(() => {
		dispatch({type: 'CLEAR_REGION'});
		cookies.remove(CookKey.Region);
	}, []);

	const skipGeolocation = useCallback(() => {
		if (!state.geoCoords) {
			geoSkippedRef.current = true;
		}
	}, [state.geoCoords]);

	return (
		<RegionContext.Provider value={{
			state,
			orderRegion: state.orderRegion,
			orderSettled: !!state.orderSettled,
			chooseOrderRegion,
			setRegion,
			setCoords,
			clearRegion,
			skipGeolocation,
		}}>
			{children}
		</RegionContext.Provider>
	);
};
